import moment from 'moment-timezone';

type BrowserHealthStatus = 'HEALTHY' | 'STALE' | 'NOT_SEEN' | 'OUTDATED' | 'UNKNOWN';

/** Per-browser extension evidence from diagnostics.browser_extension (Chrome and Edge are independent). */
export function browserHealthEntry(block: any, browser: 'Chrome' | 'Edge'): { status: BrowserHealthStatus; detail: string } {
  const entry = Array.isArray(block?.browsers)
    ? block.browsers.find((b: any) => b?.browser === browser)
    : null;
  const raw = entry?.status ?? (browser === 'Chrome' ? block?.chrome_status : block?.edge_status);
  const known: BrowserHealthStatus[] = ['HEALTHY', 'STALE', 'NOT_SEEN', 'OUTDATED'];
  const status: BrowserHealthStatus = known.includes(raw) ? raw : 'UNKNOWN';
  const lastReport = entry?.last_extension_report_at ? moment(entry.last_extension_report_at).fromNow() : null;
  const detail = [
    entry?.extension_version ? `v${entry.extension_version}` : null,
    lastReport ? `Last report ${lastReport}` : null,
    status !== 'HEALTHY' ? entry?.reason : null,
  ].filter(Boolean).join(' · ');
  return { status, detail };
}

/** Only HEALTHY is shown as healthy; missing evidence is never presented as healthy. */
export function browserHealthLevel(status: BrowserHealthStatus): 'healthy' | 'warning' | 'info' {
  if (status === 'HEALTHY') return 'healthy';
  if (status === 'STALE' || status === 'OUTDATED') return 'warning';
  return 'info';
}

/** Never render "Invalid date": unusable timestamps show as a dash. */
export function formatSeenTime(value: unknown): string {
  if (!value) return '—';
  const parsed = moment(value as string);
  return parsed.isValid() ? parsed.format('h:mm A') : '—';
}

// ---------------------------------------------------------------------------
// WP2 Phase F — Web Activity evidence presentation (backend is duration authority)
// ---------------------------------------------------------------------------

export type WebActivityDurationBasis =
  | 'FOREGROUND_VERIFIED'
  | 'LEGACY_BROWSER_INTERVAL'
  | 'TAB_LIFECYCLE_ONLY'
  | 'NONE';

export const WEB_ACTIVITY_DURATION_BASIS_VALUES: WebActivityDurationBasis[] = [
  'FOREGROUND_VERIFIED',
  'LEGACY_BROWSER_INTERVAL',
  'TAB_LIFECYCLE_ONLY',
  'NONE',
];

export function normalizeWebActivityDurationBasis(value: unknown): WebActivityDurationBasis {
  if (typeof value === 'string' && (WEB_ACTIVITY_DURATION_BASIS_VALUES as string[]).includes(value)) {
    return value as WebActivityDurationBasis;
  }
  return 'NONE';
}

/** Concise user-facing evidence label (never raw enum). */
export function webActivityEvidenceLabel(basis: unknown): string {
  switch (normalizeWebActivityDurationBasis(basis)) {
    case 'FOREGROUND_VERIFIED':
      return 'Foreground verified';
    case 'LEGACY_BROWSER_INTERVAL':
      return 'Legacy estimate';
    case 'TAB_LIFECYCLE_ONLY':
      return 'Tab activity only';
    case 'NONE':
    default:
      return 'No duration evidence';
  }
}

/** Short chip label for dense tables. */
export function webActivityEvidenceChipLabel(basis: unknown): string {
  switch (normalizeWebActivityDurationBasis(basis)) {
    case 'FOREGROUND_VERIFIED':
      return 'Verified';
    case 'LEGACY_BROWSER_INTERVAL':
      return 'Legacy';
    case 'TAB_LIFECYCLE_ONLY':
      return 'Tab only';
    case 'NONE':
    default:
      return 'No evidence';
  }
}

export function webActivityEvidenceTooltip(basis: unknown): string {
  switch (normalizeWebActivityDurationBasis(basis)) {
    case 'FOREGROUND_VERIFIED':
      return 'Active time verified using the browser state and the foreground Windows application. Idle and locked time are excluded.';
    case 'LEGACY_BROWSER_INTERVAL':
      return 'Estimated from earlier browser activity records. It may include time when the browser was not in the foreground.';
    case 'TAB_LIFECYCLE_ONLY':
      return 'TEKS TIME recorded tab activity, but there is not enough evidence to calculate active duration.';
    case 'NONE':
    default:
      return 'No reliable duration evidence is available for this activity.';
  }
}

/**
 * Display Active Duration from the backend label.
 * Lifecycle-only / none must not look like a measured zero minutes of known use.
 */
export function webActivityDurationDisplay(
  basis: unknown,
  durationLabel: unknown,
): string {
  const normalized = normalizeWebActivityDurationBasis(basis);
  if (normalized === 'TAB_LIFECYCLE_ONLY' || normalized === 'NONE') {
    return '—';
  }
  if (typeof durationLabel === 'string' && durationLabel.trim()) {
    return durationLabel.trim();
  }
  return '—';
}

/** Page title with domain fallback; never invents content. */
export function webActivityPageTitle(pageTitle: unknown, domain: unknown): string {
  if (typeof pageTitle === 'string' && pageTitle.trim()) return pageTitle.trim();
  if (typeof domain === 'string' && domain.trim()) return domain.trim();
  return '—';
}

/**
 * Sanitized URL field (`full_url` wire name). Show as supplied; never synthesize from domain.
 */
export function webActivityUrlDisplay(fullUrl: unknown): string | null {
  if (typeof fullUrl !== 'string') return null;
  const trimmed = fullUrl.trim();
  return trimmed ? trimmed : null;
}

/** Friendly close_reason for details/tooltips — not a primary column. */
export function webActivityCloseReasonLabel(reason: unknown): string | null {
  if (typeof reason !== 'string' || !reason.trim()) return null;
  const map: Record<string, string> = {
    BrowserLostForeground: 'Browser moved to background',
    IdleStarted: 'User became idle',
    WorkstationLocked: 'Workstation locked',
    TabChanged: 'Active tab changed',
    DomainChanged: 'Website changed',
    ExtensionStale: 'Browser extension stopped reporting',
    CompanionShutdown: 'TEKS TIME Companion stopped',
    WindowNotFocused: 'Browser window lost focus',
    ContentExcluded: 'Content excluded from monitoring',
    IncognitoOrPrivate: 'Private browsing',
    BrowserChanged: 'Switched browsers',
    SessionChanged: 'Windows session changed',
  };
  return map[reason] || reason;
}

export function webActivityEvidenceChipColor(
  basis: unknown,
): 'success' | 'default' | 'warning' | 'info' {
  switch (normalizeWebActivityDurationBasis(basis)) {
    case 'FOREGROUND_VERIFIED':
      return 'success';
    case 'LEGACY_BROWSER_INTERVAL':
      return 'warning';
    case 'TAB_LIFECYCLE_ONLY':
      return 'info';
    case 'NONE':
    default:
      return 'default';
  }
}

/**
 * Report-level presentation only — does not recalculate durations.
 * Never promote the whole report to Verified when any legacy/lifecycle rows exist.
 */
export function webActivityReportEvidenceSummary(
  rows: Array<{ duration_basis?: unknown }>,
  metaBasis?: unknown,
): { label: string; tooltip: string; mixed: boolean } {
  const bases = new Set(
    (rows || []).map((r) => normalizeWebActivityDurationBasis(r.duration_basis)),
  );
  if (bases.size === 0 && metaBasis != null) {
    const single = normalizeWebActivityDurationBasis(metaBasis);
    return {
      label: webActivityEvidenceLabel(single),
      tooltip: webActivityEvidenceTooltip(single),
      mixed: false,
    };
  }
  if (bases.size <= 1) {
    const only = Array.from(bases)[0] || 'NONE';
    return {
      label: webActivityEvidenceLabel(only),
      tooltip: webActivityEvidenceTooltip(only),
      mixed: false,
    };
  }
  return {
    label: 'Mixed evidence',
    tooltip:
      'This report includes more than one kind of duration evidence. Check each row — verified and legacy estimates are not the same.',
    mixed: true,
  };
}

// ---------------------------------------------------------------------------
// WP2 Phase G — Browser monitoring pipeline health presentation
// ---------------------------------------------------------------------------

export type BrowserMonitoringRuntimeStatus =
  | 'HEALTHY'
  | 'STALE'
  | 'NOT_OBSERVED'
  | 'NOT_RUNNING'
  | 'DEGRADED'
  | 'ERROR'
  | 'UNKNOWN';

const BROWSER_MONITORING_STATUS_LABELS: Record<BrowserMonitoringRuntimeStatus, string> = {
  HEALTHY: 'Healthy',
  STALE: 'Stale',
  NOT_OBSERVED: 'Not observed',
  NOT_RUNNING: 'Not running',
  DEGRADED: 'Degraded',
  ERROR: 'Error',
  UNKNOWN: 'Unknown',
};

export function normalizeBrowserMonitoringStatus(value: unknown): BrowserMonitoringRuntimeStatus {
  const raw = String(value || '').toUpperCase();
  if (raw in BROWSER_MONITORING_STATUS_LABELS) {
    return raw as BrowserMonitoringRuntimeStatus;
  }
  return 'UNKNOWN';
}

/** User-facing label — never rely on color alone. */
export function browserMonitoringStatusLabel(status: unknown): string {
  return BROWSER_MONITORING_STATUS_LABELS[normalizeBrowserMonitoringStatus(status)];
}

export function browserMonitoringHealthLevel(
  status: unknown,
): 'healthy' | 'warning' | 'critical' | 'info' {
  switch (normalizeBrowserMonitoringStatus(status)) {
    case 'HEALTHY':
      return 'healthy';
    case 'STALE':
    case 'DEGRADED':
    case 'NOT_RUNNING':
      return 'warning';
    case 'ERROR':
      return 'critical';
    default:
      return 'info';
  }
}

export function browserMonitoringSideDetail(side: any): string {
  if (!side) return 'Not observed';
  const parts = [
    side.extension_version ? `v${side.extension_version}` : null,
    side.last_browser_state_at
      ? `Last heartbeat ${moment(side.last_browser_state_at).fromNow()}`
      : 'No heartbeat',
    side.status && side.status !== 'HEALTHY' ? side.reason : null,
  ].filter(Boolean);
  return parts.join(' · ');
}

/** Compact Computer Overview line: Chrome: Healthy · Edge: Not observed */
export function browserMonitoringOverviewLabel(block: any): string | null {
  if (!block?.chrome && !block?.edge) return null;
  const chrome = browserMonitoringStatusLabel(block?.chrome?.status);
  const edge = browserMonitoringStatusLabel(block?.edge?.status);
  return `Chrome: ${chrome} · Edge: ${edge}`;
}

export function formatPendingAgeSeconds(seconds: unknown): string {
  if (seconds == null || seconds === '') return '—';
  const n = Number(seconds);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 60) return `${Math.floor(n)}s`;
  if (n < 3600) return `${Math.floor(n / 60)}m`;
  return `${Math.floor(n / 3600)}h ${Math.floor((n % 3600) / 60)}m`;
}

/** True when a diagnostics object includes forbidden Web Activity content. */
export function diagnosticsContainsSensitiveBrowserContent(payload: unknown): boolean {
  const json = JSON.stringify(payload ?? {}).toLowerCase();
  return (
    json.includes('"domain"')
    || json.includes('"page_title"')
    || json.includes('"full_url"')
    || json.includes('"sanitized_url"')
    || json.includes('"payload_json"')
    || json.includes('https://')
    || json.includes('http://')
  );
}
